import { useId } from "react";
import { cx } from "../util";
import { LOGO_HAND, LOGO_WORDMARK } from "./logoPaths";

export interface LogoProps {
  /** Mark height in px; full logos retain the legacy size scale at 3x height. */
  size?: number;
  variant?: "full" | "mark" | "wordmark";
  /** Optional dark backing tile for isolated marks. */
  tile?: boolean;
  className?: string;
  /** Empty title makes the logo decorative. */
  title?: string;
}

/** One geometry for every app surface, matching the approved marketing logo. */
function BrandVector({ size = 32, variant = "full", tile, className, title = "The GramFather" }: LogoProps) {
  const gid = `gfbrand${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const mark = variant === "mark";
  const wordOnly = variant === "wordmark";
  const height = mark ? size : wordOnly ? size : size * 3;
  const width = height * (mark ? 385 / 805 : wordOnly ? 1370 / 380 : 1370 / 805);
  const paint = `url(#${gid})`;
  return <svg className={cx(mark ? "gf-logo__mark" : "gf-logo", className)}
    width={width} height={height} viewBox={mark ? "1025 70 385 805" : wordOnly ? "205 320 1370 380" : "205 70 1370 805"}
    style={{ maxWidth: "100%", flexShrink: 0 }}
    role={title ? "img" : undefined} aria-label={title || undefined} aria-hidden={title ? undefined : true}>
    <defs><linearGradient id={gid} gradientUnits="userSpaceOnUse" x1="230" y1="0" x2="1545" y2="0">
      <stop stopColor={tile ? "#c5acff" : "var(--gf-logo-start, #c5acff)"} />
      <stop offset="1" stopColor={tile ? "#ff99c3" : "var(--gf-logo-end, #ff99c3)"} />
    </linearGradient></defs>
    {tile && mark ? <rect x="1025" y="70" width="385" height="805" rx="32" fill="#16111b" /> : null}
    <g fill={paint}>
      {!wordOnly ? <g>
        <path fillRule="evenodd" d={LOGO_HAND} />
        <g fill="none" stroke={paint} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1068 339V699L1115 747M1141 371V732M1218 389V732M1288 374V699L1233 747" />
          <rect x="1115" y="732" width="118" height="118" rx="27" strokeWidth="16" />
          <circle cx="1174" cy="791" r="32" strokeWidth="14" />
        </g><circle cx="1206" cy="758" r="10" />
      </g> : null}
      {!mark ? <path fillRule="evenodd" d={LOGO_WORDMARK} /> : null}
    </g>
  </svg>;
}

export function LogoMark(props: Omit<LogoProps, "variant">) {
  return <BrandVector {...props} variant="mark" />;
}

export function Logo(props: LogoProps) {
  return <BrandVector {...props} />;
}
